import { memo, useCallback, useEffect, useState } from "react";
import { CloseOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Spin, message } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/Store";
import { useCreateGroup } from "../../../../hooks/useChatGroup";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { GroupMemberPicker } from "./GroupMemberPicker";

interface CreateGroupModalProps {
  open: boolean;
  waiting?: boolean;
  waitingText?: string;
  onClose: () => void;
  onCreated: (groupId: string) => void;
}

export const CreateGroupModal = memo(
  ({ open, waiting = false, waitingText = "Đang đồng bộ nhóm chat mới...", onClose, onCreated }: CreateGroupModalProps) => {
    const myUserId = useSelector((state: RootState) => state.auth.userId);

    const [name, setName] = useState("");
    const [memberIds, setMemberIds] = useState<string[]>([]);

    const createGroup = useCreateGroup();
    const busy = createGroup.isPending || waiting;

    const reset = useCallback(() => {
      setName("");
      setMemberIds([]);
    }, []);

    useEffect(() => {
      if (!open) reset();
    }, [open, reset]);

    const closeModal = useCallback(() => {
      if (busy) return;
      reset();
      onClose();
    }, [busy, onClose, reset]);

    const submit = useCallback(() => {
      if (busy) return;

      const trimmed = name.trim();

      if (!trimmed) {
        message.error("Vui lòng nhập tên nhóm");
        return;
      }

      if (memberIds.length < 2) {
        message.error("Chọn ít nhất 2 thành viên");
        return;
      }

      createGroup.mutate(
        {
          name: trimmed,
          memberUserIds: memberIds,
        },
        {
          onSuccess: (detail) => {
            onCreated(detail.id);
          },
          onError: () => {
            message.error("Tạo nhóm thất bại");
          },
        },
      );
    }, [busy, createGroup, memberIds, name, onCreated]);

    return (
      <Modal
        title={null}
        open={open}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        closable={false}
        maskClosable={!busy}
        keyboard={!busy}
        zIndex={1600}
        width={560}
        centered
        className="[&_.ant-modal-content]:!overflow-hidden [&_.ant-modal-content]:!rounded-[18px] [&_.ant-modal-content]:!p-0"
      >
        <div className="relative bg-white">
          {waiting && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-white/90 backdrop-blur-[1px]">
              <Spin size="large" />
              <div className="px-6 text-center text-sm font-medium text-[#475467]">{waitingText}</div>
            </div>
          )}

          <div className="relative border-b border-[#e7edf5] bg-[#f8fbff] px-5 pb-5 pt-6">
            <button
              type="button"
              title="Đóng"
              disabled={busy}
              onClick={closeModal}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border-none bg-white text-[#667085] shadow-sm transition hover:bg-[#eef6ff] hover:text-[#0068ff] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <CloseOutlined />
            </button>

            <div className="flex items-center gap-4 pr-10">
              <CommonAvatar size={82} name={name.trim() || "Nhóm mới"} className="bg-[#0a2240] text-2xl font-semibold text-white" />

              <div className="min-w-0 flex-1">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#0068ff]">Tạo nhóm chat</div>

                <div className="flex min-w-0 items-center gap-2">
                  <span className="truncate text-xl font-semibold text-[#17212f]">{name.trim() || "Nhóm chat mới"}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#0068ff]">
                    <UsergroupAddOutlined />
                  </span>
                </div>

                <div className="mt-1 text-sm text-[#667085]">
                  {memberIds.length === 0
                    ? "Chọn tối thiểu 2 thành viên để tạo hội thoại nhóm"
                    : memberIds.length < 2
                      ? "Cần chọn thêm ít nhất 1 thành viên"
                      : `${memberIds.length + 1} thành viên`}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-5">
            <section className="rounded-xl border border-[#e7edf5] bg-white p-4 shadow-sm">
              <div className="mb-2 text-sm font-semibold text-[#17212f]">Tên nhóm</div>
              <Input
                autoFocus
                placeholder="Nhập tên nhóm..."
                maxLength={255}
                value={name}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
                onPressEnter={submit}
              />
            </section>

            <section className="rounded-xl border border-[#e7edf5] bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold text-[#17212f]">Thành viên nhóm</div>

                {memberIds.length > 0 && <span className="text-xs text-[#8a97a8]">{memberIds.length} đã chọn</span>}
              </div>

              <GroupMemberPicker
                value={memberIds}
                onChange={setMemberIds}
                excludeUserIds={myUserId ? [myUserId] : []}
                placeholder="Tìm nhân viên để thêm vào nhóm..."
              />
            </section>

            <div className="flex justify-end gap-2">
              <Button size="small" disabled={busy} onClick={closeModal}>
                Hủy
              </Button>

              <Button size="small" loading={createGroup.isPending} disabled={busy || !name.trim() || memberIds.length < 2} onClick={submit}>
                Tạo nhóm
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  },
);
